"use client"

import Link from "next/link"
import { useState } from "react"
import { useRouter } from "next/navigation"
import { Loader2 } from "lucide-react"
import { masukDenganPassword } from "@/modules/akun/actions/auth-actions"
import { useCaptcha } from "@/modules/captcha/hooks/use-captcha"
import { CaptchaField } from "@/modules/captcha/ui/captcha-field"
import { LangkahDuaMasuk } from "@/modules/akun/ui/langkah-dua-masuk"
import { FormTautanEmail } from "@/modules/akun/ui/form-tautan-email"
import { PemisahAtau } from "@/modules/akun/ui/kerangka-auth"
import { KolomPassword, KolomTeks, PesanGalat } from "@/modules/akun/ui/kolom"
import { TombolSosial } from "@/modules/akun/ui/tombol-sosial"
import type { PenyediaSosial } from "@/modules/akun/domain/tipe"

interface FormMasukProps {
  penyedia: PenyediaSosial
  pesanAwal: string | null
  lanjut: string | null
}

export function FormMasuk({ penyedia, pesanAwal, lanjut }: FormMasukProps) {
  const router = useRouter()
  const captcha = useCaptcha()
  const [mode, setMode] = useState<"password" | "tautan">("password")
  const [identitas, setIdentitas] = useState("")
  const [password, setPassword] = useState("")
  const [ingat, setIngat] = useState(true)
  const [memproses, setMemproses] = useState(false)
  const [galat, setGalat] = useState<string | null>(pesanAwal)
  const [perluDuaLangkah, setPerluDuaLangkah] = useState(false)

  const masuk = async () => {
    if (memproses) return
    setGalat(null)
    setMemproses(true)
    const hasil = await masukDenganPassword({ identitas, password, ingat, tokenCaptcha: captcha.token ?? undefined })

    if (!hasil.ok) {
      setMemproses(false)
      setGalat(hasil.pesan)
      captcha.reset()
      return
    }
    if (hasil.duaLangkah) {
      setMemproses(false)
      setPerluDuaLangkah(true)
      return
    }
    selesaiMasuk()
  }

  const selesaiMasuk = () => {
    if (lanjut) {
      window.location.assign(lanjut)
      return
    }
    router.replace("/")
    router.refresh()
  }

  if (perluDuaLangkah) {
    return (
      <LangkahDuaMasuk
        onSelesai={selesaiMasuk}
        onBatal={() => {
          setPerluDuaLangkah(false)
          setPassword("")
          captcha.reset()
        }}
      />
    )
  }

  return (
    <>
      {mode === "password" ? (
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault()
            masuk()
          }}
        >
          <KolomTeks label="Email atau username" value={identitas} onChange={setIdentitas} autoComplete="username" autoCapitalize="none" spellCheck={false} disabled={memproses} required />
          <KolomPassword label="Password" value={password} onChange={setPassword} autoComplete="current-password" disabled={memproses} />

          <div className="flex items-center justify-between gap-3">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-foreground">
              <input type="checkbox" checked={ingat} onChange={(event) => setIngat(event.target.checked)} className="h-4 w-4 rounded border-border accent-[hsl(var(--primary))]" />
              Ingat saya
            </label>
            <Link href="/lupa-password" className="text-sm font-medium text-primary hover:underline">
              Lupa password?
            </Link>
          </div>

          <CaptchaField captcha={captcha} />
          <PesanGalat pesan={galat} />

          <button type="submit" disabled={memproses || captcha.perluDiselesaikan || identitas.trim().length < 3 || password.length === 0} className="btn-primary w-full">
            {memproses ? <Loader2 className="h-4 w-4 animate-spin" /> : "Masuk"}
          </button>
        </form>
      ) : (
        <div className="space-y-4">
          <PesanGalat pesan={galat} />
          <FormTautanEmail teksTombol="Kirim tautan masuk" lanjut={lanjut} />
        </div>
      )}

      <button
        type="button"
        onClick={() => {
          setGalat(null)
          setMode((sekarang) => (sekarang === "password" ? "tautan" : "password"))
        }}
        className="block w-full text-center text-sm font-medium text-primary hover:underline"
      >
        {mode === "password" ? "Masuk lewat tautan email" : "Masuk dengan password"}
      </button>

      {(penyedia.google || penyedia.github) && (
        <>
          <PemisahAtau />
          <TombolSosial penyedia={penyedia} lanjut={lanjut} />
        </>
      )}
    </>
  )
}
